import React, { FC, useEffect, useState } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import OrganizationDetail from '../../../types/OrganizationDetail'
import { Form, Card, Row, Col, InputGroup, Button } from 'react-bootstrap'
import { Formik } from 'formik'
import { OrganizationDetailSchema } from '../../../utils/validation'
import BootstrapTable from 'react-bootstrap-table-next'
import ToolkitProvider from 'react-bootstrap-table2-toolkit'
import CircularProgress from '@material-ui/core/CircularProgress'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import RootState from '../../../types/RootState'
import moment from 'moment'
import { getOrgProviderDetails, getProviderDetails } from '../../../redux/actions/providerDetail'
import './index.css'

interface Props {
  height: number;
  details: Array<OrganizationDetail>;
  loading: boolean;
  organizationView: boolean;
  onSave: (providerDetailId: number, openings: number, providerId: number, error: any) => void;
}

const initialFilters = {
  search: '',
  sortBy: 'location asc'
}

const filterAndSortData = (details: Array<OrganizationDetail>, filters: {search: string; sortBy: string}) => {
  return details.filter((detail: OrganizationDetail) => {
    return ((detail.providerName || '').toLowerCase()).indexOf(filters.search.toLowerCase()) !== -1
  })
    .sort((a, b) => {
      if ( filters.sortBy.includes('updatedDate') ) {
        if ( filters.sortBy.includes('desc') ) {
          return a.openingsUpdatedDate === null ? 1 : ( b.openingsUpdatedDate === null ? -1 : moment(b.openingsUpdatedDate).diff(moment(a.openingsUpdatedDate)) )
        } else {
          return a.openingsUpdatedDate === null ? 1 : ( b.openingsUpdatedDate === null ? -1 : moment(a.openingsUpdatedDate).diff(moment(b.openingsUpdatedDate)) )
        }
      } else if ( filters.sortBy.includes('updatedBy') ) {
        if ( filters.sortBy.includes('asc') ) {
          return a.openingsUpdatedBy === null || a.openings === null ? 1 : ( b.openingsUpdatedBy === null || b.openings === null ? -1 : a.openingsUpdatedBy < b.openingsUpdatedBy ? -1 : 1 )
        } else {
          return a.openingsUpdatedBy === null || a.openings === null ? 1 : ( b.openingsUpdatedBy === null || b.openings === null ? -1 : b.openingsUpdatedBy < a.openingsUpdatedBy ? -1 : 1 )
        }
      } else {
        if ( filters.sortBy.includes('asc') ) {
          return a.providerName < b.providerName ? -1 : 1
        } else {
          return b.providerName < a.providerName ? -1 : 1
        }
      }
    })
}

const Detail: FC<Props> = ({ height, details, loading, organizationView, onSave }: Props) => {
  const dispatch = useDispatch()
  const [filters, setFilters] = useState(initialFilters)
  const { selectedOrganization, selectedProvider, user } = useSelector((state: RootState) => ({
    selectedOrganization: state.authentication.selectedOrganization,
    selectedProvider: state.authentication.selectedProvider,
    user: state.authentication.user.data,
  }))

  useEffect(() => {
    const token = localStorage.getItem('token') !== null && localStorage.getItem('token') !== 'undefined' ? JSON.parse(localStorage.getItem('token')|| '') : ''
    if ( selectedOrganization.organization_id && organizationView ) {
      dispatch(getOrgProviderDetails(selectedOrganization.organization_id, token))
    }
  },[selectedOrganization])  // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const token = localStorage.getItem('token') !== null && localStorage.getItem('token') !== 'undefined' ? JSON.parse(localStorage.getItem('token')|| '') : ''
    if ( selectedProvider.provider_id && !organizationView ) {
      dispatch(getProviderDetails(selectedProvider.provider_id, token))
    }
  },[selectedProvider])  // eslint-disable-line react-hooks/exhaustive-deps

  const data = loading === false ? (<div>No Location Details available</div>) : (
    <div className='organization-circular-progress'>
      <CircularProgress size={40} />
      <span>Loading data</span>
    </div>
  )

  const handleChangeFilters = (val: string, key: string) => {
    setFilters(f => ({
      ...f,
      [key]: val
    }))
  }

  const columns = [{
    dataField: 'providerName',
    text: 'Location',
  }, {
    dataField: 'openings',
    text: 'Openings',
    // eslint-disable-next-line react/display-name
    formatter: (cellContent, row) => { 
      return (
        <Formik
          initialValues={{ openings: cellContent }}
          enableReinitialize
          validationSchema={OrganizationDetailSchema}
          onSubmit={(values, { setSubmitting }) => { setSubmitting(false) }}
        >
          {({
            values,
            errors,
            handleChange,
            handleSubmit,
          }) => (
            <Form className='detailForm' onSubmit={handleSubmit}>
              <Form.Group>
                <Form.Control 
                  type='number' 
                  name='openings'
                  value={values.openings !== null ? values.openings : ''}
                  style={{ width: 150 }}
                  onChange={handleChange}
                  onBlur={() => onSave(row.providerDetailId, values.openings, row.providerId, errors.openings) }
                  disabled={user.isLakeland}
                  isInvalid={!!errors.openings}
                  min={0}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.openings}
                </Form.Control.Feedback>
              </Form.Group>
              {
                row.openingsUpdatedDate && (
                  <div className='detailOpeningsBy'>
                    <div><span style={{ fontWeight: 'bold' }}>Updated By: </span><span>{row.openingsUpdatedBy}</span></div>
                    <div><span style={{ fontWeight: 'bold' }}>Updated Date: </span><span>{moment.utc(row.openingsUpdatedDate).format('MM/DD/YYYY')}</span></div>
                  </div>
                )
              }
            </Form>
          )}
        </Formik>
      )
    }
  }]

  return (
    <div style={{ height:height-350 }}>
      <Card className='filter-container' style={{ width: '80%' }}>
        <Card.Body>
          <div className='comment-filter-and-sort' >
            <Row>
              <Form.Group as={Col} md={4} sm={12}>
                <Form.Label>Location Name</Form.Label>
                <InputGroup>
                  <InputGroup.Append>
                    <Button variant='outline-secondary' className='search-btn'><FontAwesomeIcon icon={['fas', 'search']} /></Button>
                  </InputGroup.Append>
                  <Form.Control
                    type='text'
                    name='search'
                    placeholder='Quick Search'
                    value={filters.search}
                    onChange={(event: any) => handleChangeFilters(event.target.value, event.target.name)}
                  />
                </InputGroup>
              </Form.Group>
              <Form.Group as={Col} md={4} sm={12}>
                <Form.Label>Sort By</Form.Label>
                <Form.Control
                  as='select'
                  name='sortBy'
                  value={filters.sortBy}
                  onChange={(event: any) => handleChangeFilters(event.target.value, event.target.name)}
                >
                  <option value='location asc'>Location by A - Z</option>
                  <option value='location desc'>Location by Z - A</option>
                  <option value='updatedDate asc'>Updated Date by oldest - newest</option>
                  <option value='updatedDate desc'>Updated Date by newest - oldest</option>
                  <option value='updatedBy asc'>Updated User by A - Z</option>
                  <option value='updatedBy desc'>Updated User by Z - A</option>
                </Form.Control>
              </Form.Group>
            </Row>
          </div>
        </Card.Body>
      </Card>
      <div style={{ height: '100%', width: '80%' }}>
        <ToolkitProvider
          keyField='providerId'
          bootstrap4
          data={ filterAndSortData(details, filters) }
          columns={ columns }
          search
        >
          {
            props => (
              <div style={{width: '100%', height: '100%', overflow: 'auto'}}>
                <BootstrapTable
                  id='detailTable'
                  bordered={false}
                  striped
                  condensed
                  bootstrap4
                  noDataIndication={() => (data)}
                  { ...props.baseProps }
                />
              </div>
            )
          }
        </ToolkitProvider>
      </div>
    </div>
  )
}

export default Detail